import type { Position } from '@/lib/traccar-api';

// Cargo/ambient temperature sensors: wired (temp0..tempN), BLE (bleTemp1..N) and generic.
// Device-internal (deviceTemp) and engine (coolantTemp) temperatures are not cargo readings.
const SENSOR_KEY = /^(temp\d+|bleTemp\d+|temperature)$/;

// Teltonika and others report placeholders when a sensor is missing or faulty.
const PLACEHOLDERS = new Set([327.67, -327.68, 3000, 4000, 5000]);
const MIN_VALID = -55;
const MAX_VALID = 125;

export const isValidTemperature = (value: number) =>
  Number.isFinite(value) && !PLACEHOLDERS.has(value) && value >= MIN_VALID && value <= MAX_VALID;

/** Valid temperature readings on a position, keyed by attribute (e.g. { temp1: 4.2 }). */
export const getTemperatureReadings = (position: Position): Record<string, number> => {
  const readings: Record<string, number> = {};
  for (const [key, raw] of Object.entries(position.attributes ?? {})) {
    if (!SENSOR_KEY.test(key)) continue;
    const value = Number(raw);
    if (raw !== null && raw !== '' && isValidTemperature(value)) readings[key] = value;
  }
  return readings;
};

const sensorOrder = (key: string) => {
  const match = key.match(/^(temp|bleTemp|temperature)(\d*)$/);
  const group = match?.[1] === 'temp' ? 0 : match?.[1] === 'bleTemp' ? 1 : 2;
  return group * 100 + Number(match?.[2] || 0);
};

export const sortSensorKeys = (keys: Iterable<string>) => [...keys].sort((a, b) => sensorOrder(a) - sensorOrder(b));

export interface SensorSummary {
  sensor: string;
  min: number;
  max: number;
  avg: number;
  readings: number;
  outOfRange: number;
  /** Time spent outside the allowed range, in ms (each reading counts until the next one, capped). */
  outOfRangeMs: number;
}

// A gap longer than this between readings isn't counted as time out of range.
const MAX_GAP_MS = 15 * 60 * 1000;

export const isOutOfRange = (value: number, rangeMin: number | null, rangeMax: number | null) =>
  (rangeMin != null && value < rangeMin) || (rangeMax != null && value > rangeMax);

/** Per-sensor statistics for one device's positions (any order). */
export const summarizeTemperatures = (
  positions: Position[],
  rangeMin: number | null,
  rangeMax: number | null,
): SensorSummary[] => {
  const sorted = [...positions].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
  const bySensor = new Map<string, { time: number; value: number }[]>();
  for (const position of sorted) {
    const time = new Date(position.fixTime).getTime();
    for (const [sensor, value] of Object.entries(getTemperatureReadings(position))) {
      const list = bySensor.get(sensor) ?? [];
      list.push({ time, value });
      bySensor.set(sensor, list);
    }
  }
  return sortSensorKeys(bySensor.keys()).map((sensor) => {
    const list = bySensor.get(sensor)!;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let outOfRange = 0;
    let outOfRangeMs = 0;
    list.forEach(({ time, value }, index) => {
      sum += value;
      min = Math.min(min, value);
      max = Math.max(max, value);
      if (isOutOfRange(value, rangeMin, rangeMax)) {
        outOfRange++;
        const next = list[index + 1];
        if (next) outOfRangeMs += Math.min(next.time - time, MAX_GAP_MS);
      }
    });
    return { sensor, min, max, avg: sum / list.length, readings: list.length, outOfRange, outOfRangeMs };
  });
};

const MAX_CHART_POINTS = 1500;

/**
 * Thins a sorted series for charting: keeps every n-th point plus every point that is
 * out of range, so alerts stay visible. Statistics should use the full series.
 */
export const thinForChart = <T extends Record<string, unknown>>(
  points: T[],
  sensors: string[],
  rangeMin: number | null,
  rangeMax: number | null,
): T[] => {
  if (points.length <= MAX_CHART_POINTS) return points;
  const step = Math.ceil(points.length / MAX_CHART_POINTS);
  return points.filter((point, index) =>
    index % step === 0 ||
    index === points.length - 1 ||
    sensors.some((sensor) => typeof point[sensor] === 'number' && isOutOfRange(point[sensor] as number, rangeMin, rangeMax)),
  );
};

const MERGE_GAP_MS = 10 * 60 * 1000;

export interface TemperatureExcursion {
  sensor: string;
  direction: 'high' | 'low';
  start: string;
  end: string;
  durationMs: number;
  /** Most extreme value reached (highest when high, lowest when low). */
  peak: number;
  readings: number;
}

/**
 * Alerts: consecutive readings outside the allowed range, per sensor. An excursion ends
 * at the first reading back in range (or when it flips from high to low).
 */
export const findExcursions = (
  positions: Position[],
  rangeMin: number | null,
  rangeMax: number | null,
): TemperatureExcursion[] => {
  if (rangeMin == null && rangeMax == null) return [];
  const sorted = [...positions].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
  const open = new Map<string, TemperatureExcursion>();
  const closed: TemperatureExcursion[] = [];
  const close = (sensor: string, endTime?: string) => {
    const excursion = open.get(sensor);
    if (!excursion) return;
    if (endTime) excursion.end = endTime;
    excursion.durationMs = new Date(excursion.end).getTime() - new Date(excursion.start).getTime();
    closed.push(excursion);
    open.delete(sensor);
  };
  for (const position of sorted) {
    for (const [sensor, value] of Object.entries(getTemperatureReadings(position))) {
      const direction = rangeMax != null && value > rangeMax ? 'high' : rangeMin != null && value < rangeMin ? 'low' : null;
      const current = open.get(sensor);
      if (current && current.direction !== direction) close(sensor, position.fixTime);
      if (!direction) continue;
      const excursion = open.get(sensor);
      if (excursion) {
        excursion.end = position.fixTime;
        excursion.readings++;
        excursion.peak = direction === 'high' ? Math.max(excursion.peak, value) : Math.min(excursion.peak, value);
      } else {
        open.set(sensor, { sensor, direction, start: position.fixTime, end: position.fixTime, durationMs: 0, peak: value, readings: 1 });
      }
    }
  }
  for (const sensor of [...open.keys()]) close(sensor);
  closed.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Merge alerts of the same kind interrupted by a short return into range (sensor noise).
  const merged: TemperatureExcursion[] = [];
  for (const excursion of closed) {
    const previous = [...merged].reverse().find((e) => e.sensor === excursion.sensor);
    if (
      previous &&
      previous.direction === excursion.direction &&
      new Date(excursion.start).getTime() - new Date(previous.end).getTime() <= MERGE_GAP_MS
    ) {
      previous.end = excursion.end;
      previous.durationMs = new Date(previous.end).getTime() - new Date(previous.start).getTime();
      previous.readings += excursion.readings;
      previous.peak = excursion.direction === 'high' ? Math.max(previous.peak, excursion.peak) : Math.min(previous.peak, excursion.peak);
    } else {
      merged.push(excursion);
    }
  }
  return merged;
};
